import { addDays, DateOnly } from '@if/core/date'
import { EPaymentFrequency } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import {
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledSelect,
  ControlledTextField,
  DataField,
  FieldGrid,
  FormMethods,
  useFormDialog,
  useIfUser,
} from '@if/mui'
import { Stack } from '@mui/material'
import { EPaymentMethod } from '@src/types/constants'
import {
  CalendarSimulationAction,
  ECalendarSimulationAction,
  SignedCalendarSimulation,
} from '@src/types/loan/loan-simulation'
import { MoveHorizontal } from 'lucide-react'
import { JSX, useEffect, useMemo } from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCalendarPreviewConfirm } from '../LoanCalendarActionPreview/LoanCalendarActionPreview-confirm'
import {
  useLoanCalendarActionPreview,
  useSettledValue,
} from '../LoanCalendarActionPreview/LoanCalendarActionPreview-recompute'
import LoanCalendarActionPreviewBody from '../LoanCalendarActionPreview/LoanCalendarActionPreviewBody'
import { getPaymentToRaiseIds, useValidPaymentOperationsForCalendarShift } from './LoanMoveCalendar-hooks'
import { MoveCalendarSchemaType, useMoveCalendarSchema } from './LoanMoveCalendar-schema'

interface LoanMoveCalendarBodyProps {
  form: FormMethods<MoveCalendarSchemaType>
  initialSignedSimulation: SignedCalendarSimulation
  paymentOptions: JSX.Element[]
  disabled: boolean
}

const LoanMoveCalendarBody = ({
  form,
  initialSignedSimulation,
  paymentOptions,
  disabled,
}: LoanMoveCalendarBodyProps) => {
  const { t } = useTranslation()
  const { control, setValue } = form
  const originalSimulation = initialSignedSimulation.simulation

  const user = useIfUser()
  const canBypassAcpLock = user.rights.canEditLockedPpaPayments

  const fromDate = useWatch({ control, name: 'fromDate' })
  const toDate = useWatch({ control, name: 'toDate' })
  const reason = useWatch({ control, name: 'reason' })
  const numberOfPaymentsToRaise = useWatch({ control, name: 'numberOfPaymentsToRaise' })
  const previewedSimulation = useWatch({ control, name: 'previewedSimulation' })
  const { isValid } = useFormState({ control })

  const movesForward = Boolean(fromDate && toDate && fromDate.getTime() < toDate.getTime())

  // Moving a payment backwards can never leave a gap to cover, so the count the agent may
  // have typed for a forward move stops applying the moment the move turns around.
  useEffect(() => {
    if (!movesForward) setValue('numberOfPaymentsToRaise', 0, { shouldValidate: true })
  }, [movesForward, setValue])

  // The recompute follows the fields once they settle, and settling is what opens Confirm —
  // so what the agent is about to apply is always the calendar they are looking at.
  const settledFromDate = useSettledValue(fromDate)
  const settledToDate = useSettledValue(toDate)
  const settledReason = useSettledValue(reason)
  const settledCount = useSettledValue(numberOfPaymentsToRaise)

  const action = useMemo<CalendarSimulationAction | null>(
    () =>
      isValid && settledFromDate && settledToDate && settledReason && settledCount !== undefined
        ? {
            actionType: ECalendarSimulationAction.MoveCalendar,
            fromDate: settledFromDate,
            toDate: settledToDate,
            reason: settledReason,
            paymentToRaiseIds: getPaymentToRaiseIds(originalSimulation, settledFromDate, settledCount),
          }
        : null,
    [isValid, originalSimulation, settledCount, settledFromDate, settledReason, settledToDate],
  )

  const error = useLoanCalendarActionPreview({ form, initialSignedSimulation, action })

  const minShiftPaymentDate = useMemo(
    () => originalSimulation.getNextValidActionDate(EPaymentMethod.PreAuthorizedDebit, canBypassAcpLock),
    [originalSimulation, canBypassAcpLock],
  )
  const maxShiftPaymentDate = useMemo(() => {
    if (fromDate == null) return minShiftPaymentDate
    const paymentConfig = originalSimulation.getRepaymentConfigAtDate(fromDate)

    if (paymentConfig == null) return minShiftPaymentDate

    const nextPayment = originalSimulation.getNextPaymentAfterDate(fromDate)
    let maxDate = fromDate
    if (paymentConfig.frequency === EPaymentFrequency.Monthly) {
      maxDate = addDays(fromDate, 30)
    } else if (paymentConfig.frequency === EPaymentFrequency.BiWeekly) {
      maxDate = addDays(fromDate, 7)
    }

    if (!nextPayment) return maxDate

    return nextPayment.effectiveDate.getTime() < maxDate.getTime() ? addDays(nextPayment.effectiveDate, -1) : maxDate
  }, [originalSimulation, fromDate, minShiftPaymentDate])

  // What the raise actually costs, read back off the preview rather than predicted: the first
  // raised payment is the one the agent sees change.
  const amountToRaise = useMemo(() => {
    if (!fromDate || !previewedSimulation) return null
    const [raisedId] = getPaymentToRaiseIds(originalSimulation, fromDate, numberOfPaymentsToRaise)
    if (raisedId == null) return null
    const before = originalSimulation.customerPaymentOperations.find((op) => op.operation.id === raisedId)
    const after = previewedSimulation.simulation.customerPaymentOperations.find((op) => op.operation.id === raisedId)
    if (!before || !after) return null
    return after.operation.amount - before.operation.amount
  }, [fromDate, numberOfPaymentsToRaise, originalSimulation, previewedSimulation])

  return (
    <Stack spacing={2}>
      <LoanCalendarActionPreviewBody
        simulationBefore={originalSimulation}
        preview={previewedSimulation}
        recomputing={isValid && previewedSimulation === null}
        actionDate={toDate}
        error={error}
      />

      <FieldGrid columns={{ xs: 1, sm: 2 }}>
        <ControlledSelect
          name="fromDate"
          control={control}
          options={paymentOptions}
          parseValue={(value: string | null) => (value ? new DateOnly(value) : null)}
          label={t('loan.payment.moveCalendarDialog.from')}
          onChange={() => form.resetField('toDate')}
          disableEmptyOption
          disabled={disabled}
        />
        <ControlledDateOnlyPicker
          name="toDate"
          control={control}
          label={t('loan.payment.moveCalendarDialog.toDate')}
          onChange={() => void form.trigger('fromDate')}
          disabled={disabled || fromDate === null}
          minDate={minShiftPaymentDate}
          maxDate={maxShiftPaymentDate}
          referenceDate={fromDate ?? undefined}
        />
        <ControlledTextField
          label={t('loan.payment.moveCalendarDialog.reason')}
          name="reason"
          control={control}
          disabled={disabled}
          span="full"
        />
        <ControlledNumberField
          name="numberOfPaymentsToRaise"
          control={control}
          label={t('loan.payment.moveCalendarDialog.numberOfPaymentsToRaise')}
          disabled={disabled || !movesForward}
          disableDecimals
        />
        <DataField
          label={t('loan.payment.moveCalendarDialog.amountToRaiseFor')}
          value={amountToRaise === null ? null : formatCurrency(amountToRaise)}
        />
      </FieldGrid>
    </Stack>
  )
}

export const useLoanMoveCalendarDialog = (
  initialSignedSimulation: SignedCalendarSimulation,
  isLockedByOther: boolean,
) => {
  const { t } = useTranslation()
  const schema = useMoveCalendarSchema(initialSignedSimulation.simulation)
  const paymentOptions = useValidPaymentOperationsForCalendarShift(initialSignedSimulation.simulation)
  const confirm = useCalendarPreviewConfirm(isLockedByOther)

  return useFormDialog<MoveCalendarSchemaType, void, SignedCalendarSimulation>({
    icon: MoveHorizontal,
    title: t('loan.payment.moveCalendarDialog.title'),
    confirmLabel: t('common.confirm'),
    maxWidth: 'sm',
    fullWidth: true,
    schema,
    // Confirming stages a pending change on the page, it saves nothing — the page says so.
    successMessage: false,
    ...confirm,
    content: ({ form }) => (
      <LoanMoveCalendarBody
        form={form}
        initialSignedSimulation={initialSignedSimulation}
        paymentOptions={paymentOptions}
        disabled={isLockedByOther}
      />
    ),
    onSubmit: (values) => values.previewedSimulation ?? false,
  })
}
